import { AxiosError } from "axios";

type StrapiErrorPayload = {
  error?: {
    status?: number;
    name?: string;
    message?: string;
  };
};

export type AuthenticationFlow = "local-login" | "google-oauth-callback";

export type AuthenticationErrorKind =
  | "oauth-email-already-taken-local-account"
  | "local-login-invalid-credentials-or-oauth-account"
  | "generic-authentication-error";

type ParsedAuthenticationError = {
  status?: number;
  name?: string;
  message?: string;
};

export const AUTHENTICATION_ERROR_MESSAGES = {
  oauthEmailAlreadyTakenLocalAccount:
    "Este e-mail já está cadastrado com senha. Use seu e-mail e senha para entrar.",
  localLoginInvalidCredentialsOrOAuthAccount:
    "Não foi possível entrar com e-mail e senha. Verifique suas credenciais ou use o login com Google.",
  localLoginGeneric:
    "Não foi possível entrar com e-mail e senha. Tente novamente em instantes.",
  googleOAuthGeneric: "Não foi possível concluir o login. Tente novamente.",
} as const;

export type MappedAuthenticationError = {
  kind: AuthenticationErrorKind;
  message: string;
  rawStatus?: number;
  rawName?: string;
  rawMessage?: string;
};

function parseAuthenticationError(error: unknown): ParsedAuthenticationError {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as StrapiErrorPayload | undefined;

    return {
      status: payload?.error?.status ?? error.response?.status,
      name: payload?.error?.name,
      message: payload?.error?.message,
    };
  }

  if (typeof error === "object" && error !== null) {
    const maybeResponse = (
      error as { response?: { status?: number; data?: StrapiErrorPayload } }
    ).response;
    const payload = maybeResponse?.data;

    return {
      status: payload?.error?.status ?? maybeResponse?.status,
      name: payload?.error?.name,
      message: payload?.error?.message,
    };
  }

  return {};
}

export function mapAuthenticationError(
  error: unknown,
  flow: AuthenticationFlow
): MappedAuthenticationError {
  const parsedError = parseAuthenticationError(error);

  if (
    flow === "google-oauth-callback" &&
    parsedError.status === 400 &&
    parsedError.name === "ApplicationError" &&
    parsedError.message === "Email is already taken."
  ) {
    return {
      kind: "oauth-email-already-taken-local-account",
      message: AUTHENTICATION_ERROR_MESSAGES.oauthEmailAlreadyTakenLocalAccount,
      rawStatus: parsedError.status,
      rawName: parsedError.name,
      rawMessage: parsedError.message,
    };
  }

  if (
    flow === "local-login" &&
    parsedError.status === 400 &&
    parsedError.name === "ValidationError" &&
    parsedError.message === "Invalid identifier or password"
  ) {
    return {
      kind: "local-login-invalid-credentials-or-oauth-account",
      message:
        AUTHENTICATION_ERROR_MESSAGES.localLoginInvalidCredentialsOrOAuthAccount,
      rawStatus: parsedError.status,
      rawName: parsedError.name,
      rawMessage: parsedError.message,
    };
  }

  return {
    kind: "generic-authentication-error",
    message:
      flow === "local-login"
        ? AUTHENTICATION_ERROR_MESSAGES.localLoginGeneric
        : AUTHENTICATION_ERROR_MESSAGES.googleOAuthGeneric,
    rawStatus: parsedError.status,
    rawName: parsedError.name,
    rawMessage: parsedError.message,
  };
}
